<template>
    <div class="empty-state" :class="stateClass">
        <div class="empty-content">
            <div class="empty-icon">
                <v-avatar size="120" :color="iconBackgroundColor">
                    <v-icon size="60" :color="iconColor">{{ iconName }}</v-icon>
                </v-avatar>
            </div>
            
            <div class="empty-text">
                <h2 class="empty-title">{{ title }}</h2>
                <p class="empty-subtitle">
                    {{ subtitle }}
                </p>
                
                <div class="empty-reasons" v-if="reasons && reasons.length > 0">
                    <h3 class="reasons-title">{{ reasonsTitle }}</h3>
                    <ul class="reasons-list">
                        <li v-for="(reason, index) in reasons" :key="index">{{ reason }}</li>
                    </ul>
                </div>
                
                <div v-if="errorDetails" class="error-details">
                    <v-alert type="error" text dense>
                        {{ errorDetails }}
                    </v-alert>
                </div>
            </div>
            
            <div class="empty-actions">
                <v-btn 
                    color="primary" 
                    large
                    @click="retryFetch"
                    :loading="isRetrying"
                    class="retry-btn"
                >
                    <v-icon left>mdi-refresh</v-icon>
                    Erneut versuchen
                </v-btn>
                
                <v-btn 
                    text 
                    color="grey darken-1"
                    @click="goToOverview"
                    class="overview-btn"
                >
                    <v-icon left>mdi-view-dashboard</v-icon>
                    Zur Übersicht
                </v-btn>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'EmptyState',
    props: {
        errorType: {
            type: String,
            default: 'empty', // 'empty', 'not_found', 'not_in_shelf', 'not_ready', 'server_error'
        },
        errorMessage: {
            type: String,
            default: ''
        },
        orderId: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            isRetrying: false,
            windowWidth: window.innerWidth
        }
    },
    mounted() {
        window.addEventListener('resize', this.handleResize);
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.handleResize);
    },
    computed: {
        isSmallDevice() {
            return this.windowWidth < 768;
        },
        stateClass() {
            return `state-${this.errorType}`;
        },
        iconName() {
            const icons = {
                'empty': 'mdi-package-variant',
                'not_found': 'mdi-file-search',
                'not_in_shelf': 'mdi-package-variant-closed',
                'not_ready': 'mdi-clock-alert',
                'server_error': 'mdi-alert-circle'
            };
            return icons[this.errorType] || 'mdi-package-variant';
        },
        iconBackgroundColor() {
            const colors = {
                'empty': 'grey lighten-3',
                'not_found': 'orange lighten-3',
                'not_in_shelf': 'red lighten-4',
                'not_ready': 'amber lighten-3',
                'server_error': 'red lighten-3'
            };
            return colors[this.errorType] || 'grey lighten-3';
        },
        iconColor() {
            const colors = {
                'empty': 'grey darken-1',
                'not_found': 'orange darken-2',
                'not_in_shelf': 'red darken-2',
                'not_ready': 'amber darken-3',
                'server_error': 'red darken-2'
            };
            return colors[this.errorType] || 'grey darken-1';
        },
        title() {
            if (this.errorType === 'not_found' && this.orderId) {
                return `Bestellung ${this.orderId} nicht gefunden`;
            }
            const titles = {
                'empty': 'Keine Bestellungen verfügbar',
                'not_found': 'Bestellung nicht gefunden',
                'not_in_shelf': 'Bestellung nicht im Regal',
                'not_ready': 'Bestellung noch nicht bereit',
                'server_error': 'Serverfehler'
            };
            return titles[this.errorType] || 'Keine Bestellungen verfügbar';
        },
        subtitle() {
            if (this.errorMessage) {
                return this.errorMessage;
            }
            if (this.errorType === 'not_in_shelf' && this.orderId) {
                return `Die Bestellung ${this.orderId} befindet sich nicht im Multicart-Regal.`;
            }
            if (this.errorType === 'not_ready' && this.orderId) {
                return `Die Bestellung ${this.orderId} ist noch nicht bereit zum Packen.`;
            }
            const subtitles = {
                'empty': 'Aktuell sind keine Multicart-Bestellungen bereit zum Packen.',
                'not_found': 'Die angeforderte Bestellung konnte nicht gefunden werden.',
                'not_in_shelf': 'Diese Bestellung befindet sich nicht im Multicart-Regal.',
                'not_ready': 'Die Bestellung ist noch nicht vollständig und kann nicht gepackt werden.',
                'server_error': 'Es ist ein Fehler beim Laden der Bestellung aufgetreten.'
            };
            return subtitles[this.errorType] || 'Aktuell sind keine Multicart-Bestellungen bereit zum Packen.';
        },
        reasonsTitle() {
            const titles = {
                'empty': 'Mögliche Gründe:',
                'not_found': 'Was könnte das Problem sein?',
                'not_in_shelf': 'Mögliche Ursachen:',
                'not_ready': 'Warum ist die Bestellung nicht bereit?',
                'server_error': 'Mögliche Ursachen:'
            };
            return titles[this.errorType] || 'Mögliche Gründe:';
        },
        reasons() {
            const reasonsList = {
                'empty': [
                    'Alle Bestellungen sind bereits in Bearbeitung',
                    'Artikel befinden sich noch nicht vollständig im Regal',
                    'Keine neuen Bestellungen verfügbar'
                ],
                'not_found': [
                    'Die Bestellung existiert nicht in der Datenbank',
                    'Die Bestellnummer wurde falsch eingegeben',
                    'Die Bestellung wurde bereits verarbeitet'
                ],
                'not_in_shelf': [
                    'Die Artikel wurden noch nicht eingelagert',
                    'Die Bestellung ist für einen anderen Bereich bestimmt',
                    'Die Regalplätze wurden noch nicht zugewiesen'
                ],
                'not_ready': [
                    'Nicht alle Artikel befinden sich im Regal',
                    'Einige Artikel sind von einem anderen Benutzer gesperrt',
                    'Es fehlen noch Produkte für diese Bestellung'
                ],
                'server_error': [
                    'Temporärer Serverfehler',
                    'Datenbankverbindung unterbrochen',
                    'Bitte versuchen Sie es erneut'
                ]
            };
            return reasonsList[this.errorType] || [];
        },
        errorDetails() {
            if (this.errorType === 'server_error' && this.errorMessage) {
                return `Fehlerdetails: ${this.errorMessage}`;
            }
            return null;
        }
    },
    methods: {
        async retryFetch() {
            this.isRetrying = true;
            
            try {
                await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate delay
                this.$emit('retry');
            } finally {
                this.isRetrying = false;
            }
        },
        
        goToOverview() {
            // On small devices (mobile/tablet), redirect to task choice
            // On desktop, redirect to home
            if (this.isSmallDevice) {
                this.$router.push('/warehouse/task-choice');
            } else {
                this.$router.push('/');
            }
        },

        handleResize() {
            this.windowWidth = window.innerWidth;
        }
    }
}
</script>

<style scoped>
.empty-state {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100vh;
    background: linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%);
    padding: 32px;
}

.empty-content {
    text-align: center;
    max-width: 500px;
}

.empty-icon {
    margin-bottom: 32px;
    animation: pulse 2s ease-in-out infinite;
    display: flex;
    justify-content: center;
    align-items: center;
}

.empty-icon .v-avatar {
    border-radius: 50% !important;
    flex-shrink: 0;
}

.empty-text {
    margin-bottom: 40px;
}

.empty-title {
    font-size: 32px;
    font-weight: 600;
    color: #2c3e50;
    margin-bottom: 12px;
}

.empty-subtitle {
    font-size: 18px;
    color: #7f8c8d;
    line-height: 1.6;
    margin-bottom: 32px;
}

.empty-reasons {
    background: rgba(255, 255, 255, 0.8);
    border-radius: 12px;
    padding: 24px;
    text-align: left;
    margin-bottom: 16px;
}

.reasons-title {
    font-size: 16px;
    font-weight: 600;
    color: #34495e;
    margin-bottom: 12px;
}

.reasons-list {
    margin: 0;
    padding-left: 20px;
    color: #5d6d7e;
    font-size: 14px;
    line-height: 1.8;
}

.reasons-list li {
    margin-bottom: 8px;
}

.empty-actions {
    display: flex;
    flex-direction: column;
    gap: 16px;
    align-items: center;
}

.retry-btn {
    text-transform: none;
    border-radius: 8px;
    font-weight: 600;
    min-width: 200px;
}

.overview-btn {
    text-transform: none;
    border-radius: 8px;
}

@keyframes pulse {
    0%, 100% {
        transform: scale(1);
        opacity: 0.8;
    }
    50% {
        transform: scale(1.05);
        opacity: 1;
    }
}

/* Responsive */
@media (max-width: 768px) {
    .empty-state {
        padding: 24px 16px;
        height: 100vh;
        height: 100dvh; /* Use dynamic viewport height for mobile */
    }

    .empty-content {
        max-width: 100%;
        display: flex;
        flex-direction: column;
        justify-content: center;
        height: 100%;
    }

    .empty-icon {
        margin-bottom: 24px;
    }

    .empty-icon .v-avatar {
        width: 100px !important;
        height: 100px !important;
        min-width: 100px !important;
        min-height: 100px !important;
        max-width: 100px !important;
        max-height: 100px !important;
    }

    .empty-icon .v-icon {
        font-size: 50px !important;
    }

    .empty-title {
        font-size: 26px;
        margin-bottom: 16px;
    }

    .empty-subtitle {
        font-size: 16px;
        margin-bottom: 24px;
    }

    .empty-reasons {
        padding: 16px;
        margin-bottom: 24px;
    }

    .reasons-title {
        font-size: 15px;
    }

    .reasons-list {
        font-size: 14px;
        line-height: 1.6;
    }

    .empty-actions {
        flex-direction: column;
        gap: 12px;
        width: 100%;
    }

    .retry-btn,
    .overview-btn {
        width: 100%;
        min-width: unset;
        height: 56px !important;
        font-size: 16px !important;
    }

    .retry-btn {
        min-height: 56px;
    }
}

@media (max-width: 480px) {
    .empty-state {
        padding: 16px;
    }

    .empty-icon {
        margin-bottom: 20px;
    }

    .empty-icon .v-avatar {
        width: 80px !important;
        height: 80px !important;
        min-width: 80px !important;
        min-height: 80px !important;
        max-width: 80px !important;
        max-height: 80px !important;
    }

    .empty-icon .v-icon {
        font-size: 40px !important;
    }

    .empty-title {
        font-size: 22px;
        margin-bottom: 12px;
    }

    .empty-subtitle {
        font-size: 15px;
        margin-bottom: 20px;
    }

    .empty-reasons {
        padding: 14px;
        margin-bottom: 20px;
    }

    .reasons-title {
        font-size: 14px;
        margin-bottom: 10px;
    }

    .reasons-list {
        font-size: 13px;
        padding-left: 18px;
    }

    .reasons-list li {
        margin-bottom: 6px;
    }

    .empty-actions {
        gap: 10px;
    }

    .retry-btn,
    .overview-btn {
        height: 52px !important;
        font-size: 15px !important;
    }
}

/* Mobile landscape optimization */
@media (max-width: 768px) and (max-height: 500px) {
    .empty-state {
        height: auto;
        min-height: 100vh;
        padding: 16px;
    }

    .empty-icon {
        margin-bottom: 16px;
    }

    .empty-icon .v-avatar {
        width: 70px !important;
        height: 70px !important;
        min-width: 70px !important;
        min-height: 70px !important;
        max-width: 70px !important;
        max-height: 70px !important;
    }

    .empty-icon .v-icon {
        font-size: 35px !important;
    }

    .empty-title {
        font-size: 20px;
        margin-bottom: 10px;
    }

    .empty-subtitle {
        font-size: 14px;
        margin-bottom: 16px;
    }

    .empty-text {
        margin-bottom: 20px;
    }

    .empty-reasons {
        padding: 12px;
        margin-bottom: 16px;
    }

    .reasons-title {
        font-size: 13px;
        margin-bottom: 8px;
    }

    .reasons-list {
        font-size: 12px;
        line-height: 1.5;
    }

    .reasons-list li {
        margin-bottom: 4px;
    }

    .retry-btn,
    .overview-btn {
        height: 48px !important;
        font-size: 14px !important;
    }
}
</style>
